import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import pocketbaseService from '../services/pocketbase';

const { FiX, FiSave, FiEye } = FiIcons;

const RecordModal = ({ mode, collection, record, schema, onClose }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (record) {
      setFormData({ ...record });
    } else {
      // No GretaBase, apenas o ID é obrigatório. Inicializamos tudo vazio/nulo.
      const initialData = {};
      schema.forEach(field => {
        if (!field.system) {
          initialData[field.name] = field.type === 'bool' ? false : (field.type === 'json' ? null : '');
        }
      });
      setFormData(initialData);
    }
  }, [record, schema]);

  const handleInputChange = (fieldName, value, fieldType) => {
    let processedValue = value;
    if (fieldType === 'number') {
      processedValue = value === '' ? null : Number(value);
    } else if (fieldType === 'bool') {
      processedValue = Boolean(value);
    } else if (fieldType === 'json') {
      try {
        processedValue = value === '' ? null : JSON.parse(value);
      } catch {
        processedValue = value;
      }
    }
    setFormData(prev => ({ ...prev, [fieldName]: processedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Limpeza de campos de sistema para envio
      const submitData = { ...formData };
      const systemFields = ['id', 'created', 'updated', 'collectionId', 'collectionName'];
      systemFields.forEach(f => delete submitData[f]);

      if (mode === 'create') {
        await pocketbaseService.createRecord(collection.name, submitData);
      } else if (mode === 'edit') {
        await pocketbaseService.updateRecord(collection.name, record.id, submitData);
      }
      onClose(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    // Ignorar campos de sistema no formulário de edição/criação direta
    if (field.system && field.name !== 'id') return null;
    
    const value = formData[field.name] ?? '';
    const isReadOnly = mode === 'view' || field.system;

    return (
      <div key={field.name} className="mb-5">
        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
          {field.name} {field.required && <span className="text-red-500">*</span>}
          <span className="ml-2 lowercase font-normal text-slate-300">({field.type})</span>
        </label>
        
        {field.type === 'bool' ? (
          <button
            type="button"
            disabled={isReadOnly}
            onClick={() => handleInputChange(field.name, !value, field.type)}
            className={`w-12 h-6 rounded-full transition-colors relative ${value ? 'bg-indigo-600' : 'bg-slate-200'} ${isReadOnly ? 'opacity-50' : ''}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${value ? 'left-7' : 'left-1'}`} />
          </button>
        ) : field.type === 'json' ? (
          <textarea
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
            onChange={(e) => handleInputChange(field.name, e.target.value, field.type)}
            readOnly={isReadOnly}
            rows={4}
            className="w-full p-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-xl outline-none font-mono text-xs"
          />
        ) : (
          <input
            type={field.type === 'number' ? 'number' : 'text'}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value, field.type)}
            readOnly={isReadOnly}
            className="w-full p-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500 rounded-xl outline-none font-bold text-sm"
          />
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-8 border-b border-slate-50 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
              <SafeIcon icon={mode === 'view' ? FiEye : FiSave} />
            </div>
            <h2 className="text-xl font-black text-slate-800">
              {mode === 'create' ? 'Novo Registro' : mode === 'edit' ? 'Refinar Registro' : 'Detalhes'}
            </h2>
          </div>
          <button onClick={() => onClose()} className="p-2 hover:bg-slate-50 rounded-xl text-slate-300 transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="p-8 overflow-y-auto flex-1">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-100 rounded-2xl text-red-600 text-xs font-bold flex items-center space-x-2">
              <SafeIcon icon={FiIcons.FiAlertCircle} />
              <span>{error}</span>
            </div>
          )}
          <form id="record-form" onSubmit={handleSubmit}>
            {schema.map(field => renderField(field))}
          </form>
        </div>

        {mode !== 'view' && (
          <div className="p-8 bg-slate-50/50 border-t border-slate-100 flex space-x-3">
            <button type="button" onClick={() => onClose()} className="flex-1 py-4 bg-white border border-slate-200 text-slate-500 rounded-2xl font-black text-sm">Cancelar</button>
            <button form="record-form" type="submit" disabled={loading} className="flex-[2] bg-indigo-600 text-white py-4 rounded-2xl font-black text-sm shadow-xl shadow-indigo-100 disabled:opacity-50">
              {loading ? 'Processando...' : 'Confirmar Alterações'}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default RecordModal;